import React, { useState } from 'react';
import { UserCircle2, ShieldCheck, Landmark, Eye, EyeOff, ZoomIn, History } from 'lucide-react';
import MaskedMobile from './MaskedMobile';
import SmartImage from './SmartImage';
import ImageViewerModal from './ImageViewerModal';
import UserHistoryModal from './UserHistoryModal';
import { formatDateToDdmmyyyy } from '../../utils/format';

// The three investor sections — Basic, KYC and Bank Details — shown the same way on every
// dashboard screen that displays an investor (or SpecialCategory) user. Field names vary a
// little by endpoint (GET /users/customers/{mobile}, /users/referrals/{mobile}, order
// investor), so every read below tries each known spelling.

export type ProfileSection = 'basic' | 'kyc' | 'bank';

// Role can be a comma-separated list (e.g. "Investor,Employee").
export const isInvestorRole = (role?: string | null): boolean =>
    String(role || '')
        .split(',')
        .map(r => r.trim().toLowerCase())
        .some(r => r === 'investor' || r === 'specialcategory');

const isBlank = (v: unknown) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
const fmtDate = (v?: string | null) => (isBlank(v) ? undefined : formatDateToDdmmyyyy(v));

// One cell of a bordered field grid. The grid's -mr-px/-mb-px plus the container's
// overflow-hidden hide the outer edge of the last column/row, so only inner dividers show.
const Field: React.FC<{ label: string; value?: React.ReactNode; mono?: boolean; wide?: boolean }> = ({ label, value, mono, wide }) => {
    const empty = isBlank(value);
    return (
        <div className={`flex flex-col gap-1.5 min-w-0 px-4 py-3.5 border-r border-b border-slate-100 ${wide ? 'col-span-2' : ''}`}>
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
            <span className={`text-sm font-medium break-words [overflow-wrap:anywhere] leading-relaxed ${empty ? 'text-slate-300' : 'text-slate-900'} ${mono && !empty ? 'font-mono tracking-wide' : ''}`}>
                {empty ? '—' : value}
            </span>
        </div>
    );
};

// `docs` renders as the last full-width row of the same box, holding the document images.
const FieldGrid: React.FC<{ children: React.ReactNode; docs?: React.ReactNode }> = ({ children, docs }) => (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 -mr-px -mb-px">
            {children}
            {docs && (
                <div className="col-span-full px-4 py-4 border-r border-b border-slate-100">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{docs}</div>
                </div>
            )}
        </div>
    </div>
);

const MaskedText: React.FC<{ value: string }> = ({ value }) => {
    const [shown, setShown] = useState(false);
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className="font-mono tracking-wide">{shown ? value : `•••• ${value.slice(-4)}`}</span>
            <button type="button" onClick={() => setShown(s => !s)} title={shown ? 'Hide' : 'Show'}
                className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                {shown ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
        </span>
    );
};

const DocTile: React.FC<{ label: string; url?: string | null; onOpen: (url: string) => void }> = ({ label, url, onOpen }) => (
    <div className="flex flex-col gap-2 min-w-0">
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
        <button type="button" disabled={!url} onClick={() => url && onOpen(url)}
            className="group relative h-28 w-full rounded-xl overflow-hidden border border-slate-200 bg-slate-50 enabled:hover:border-[#4a7c59]/50 enabled:cursor-pointer disabled:cursor-default transition-colors">
            <SmartImage src={url} alt={label} className="w-full h-full object-cover"
                fallback={<div className="w-full h-full flex items-center justify-center text-xs font-medium text-slate-400">No image uploaded</div>} />
            {url && (
                <span className="absolute inset-0 flex items-center justify-center bg-slate-900/0 group-hover:bg-slate-900/30 transition-colors">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5 text-slate-800 text-xs font-semibold bg-white/95 px-3 py-1.5 rounded-full shadow-sm">
                        <ZoomIn size={13} /> View
                    </span>
                </span>
            )}
        </button>
    </div>
);

const Section: React.FC<{
    icon: React.ElementType; title: string; action?: React.ReactNode; children: React.ReactNode;
}> = ({ icon: Icon, title, action, children }) => (
    <section>
        <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2.5 min-w-0 text-[#4a7c59]">
                <Icon size={20} className="shrink-0" />
                <h3 className="font-semibold text-lg text-slate-800">{title}</h3>
            </div>
            {action && <div className="shrink-0">{action}</div>}
        </div>
        {children}
    </section>
);

const Note: React.FC<{ text: string }> = ({ text }) => (
    <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-5 text-sm text-slate-500 text-center">{text}</p>
);

export interface UserProfileCardProps {
    user: any;
    className?: string;
    // Header bar title. Pass hideHeader where the page already titles this block.
    title?: string;
    hideHeader?: boolean;
    // 'card' (default): all sections inside one card. 'plain': no outer card, for embedding
    // inside an existing card.
    variant?: 'card' | 'plain';
    // Which sections to show. Defaults to all three for Investor / SpecialCategory users and
    // Basic Details only for everyone else.
    sections?: ProfileSection[];
    basicActionSlot?: React.ReactNode;
    kycActionSlot?: React.ReactNode;
    bankActionSlot?: React.ReactNode;
    /** @deprecated use basicActionSlot */
    personalInfoActionSlot?: React.ReactNode;
    // Shows the "History" button that opens the user's change log. Off by default:
    // profile cards are view-only; the KYC queue keeps its own history.
    showHistory?: boolean;
    // Shows the "Updated By" / "Updated Date" fields under KYC. On by default.
    showUpdated?: boolean;
}

const UserProfileCard: React.FC<UserProfileCardProps> = ({
    user, className = '', title = 'User Profile', hideHeader, variant = 'card', sections,
    basicActionSlot, kycActionSlot, bankActionSlot, personalInfoActionSlot, showHistory = false, showUpdated = true,
}) => {
    const [viewingImage, setViewingImage] = useState<string | null>(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    if (!user) return null;

    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
    const historyButton = showHistory && user.mobile ? (
        <button type="button" onClick={() => setHistoryOpen(true)} title="View change history"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#4a7c59] bg-[#f0f9f4] border border-[#4a7c59]/20 hover:bg-[#4a7c59]/10 transition-colors cursor-pointer">
            <History size={13} /> History
        </button>
    ) : null;
    const headerShown = variant === 'card' && !hideHeader;
    // Without the header bar, the button sits with the Basic Details actions.
    const basicAction = (basicActionSlot ?? personalInfoActionSlot ?? null);
    const basicActions = !headerShown && historyButton
        ? <div className="flex items-center gap-2">{historyButton}{basicAction}</div>
        : basicAction;

    const show = new Set<ProfileSection>(sections || (isInvestorRole(user.role) ? ['basic', 'kyc', 'bank'] : ['basic']));
    // Some endpoints nest bank fields under `bank_details`.
    const bank = { ...(user.bank_details || {}), ...user };
    const pick = (...keys: string[]) => keys.map(k => bank[k]).find(v => !isBlank(v));

    const referredByCode = pick('refered_by_code', 'referred_by_code', 'referd_by_refferal_code');
    const onboardedBy = [user.onboarded_by_name, user.onboarded_by_role ? `(${user.onboarded_by_role})` : null].filter(Boolean).join(' ');
    const updatedBy = [user.updated_by_name, user.updated_by_role ? `(${user.updated_by_role})` : null].filter(Boolean).join(' ');
    const accountNumber = pick('account_number');

    const hasKyc = [
        'dob', 'gender', 'occupation', 'aadhar_number', 'panCardNumber', 'pan_number', 'aadhar_address',
        'permanent_address', 'address', 'current_address', 'city', 'state', 'pincode',
        'aadhar_front_image_url', 'aadhar_back_image_url', 'panCardUrl',
    ].some(k => !isBlank(user[k]));
    const hasBank = [
        'account_holder_name', 'account_number', 'ifsc_code', 'bank_name', 'passbook_image_url', 'cancelled_cheque_url',
    ].some(k => !isBlank(bank[k]));

    const mobileValue = (m?: string, cc?: string) =>
        m ? <MaskedMobile mobile={m} countryCode={cc} className="text-sm font-medium text-slate-900" /> : undefined;

    const body = (
        <div className="space-y-8">
            {show.has('basic') && (
                <Section icon={UserCircle2} title="Basic Details" action={basicActions || undefined}>
                    <FieldGrid>
                        <Field label="User ID" value={user.user_id} mono />
                        <Field label="First Name" value={user.first_name} />
                        <Field label="Last Name" value={user.last_name} />
                        <Field label="Mobile" value={mobileValue(user.mobile, user.country_code)} />
                        <Field label="Email" value={user.email} />
                        <Field label="Role" value={user.role} />
                        <Field label="Referral Code" value={pick('referral_code', 'referralcode')} mono />
                        <Field label="Joined On" value={fmtDate(user.user_created_date)} />
                        <Field label="Referred By Name" value={user.refered_by_name} />
                        <Field label="Referred By Mobile" value={mobileValue(user.refered_by_mobile, user.refered_by_country_code)} />
                        <Field label="Referred By Code" value={referredByCode} mono />
                        {(user.onboarded_by_mobile || onboardedBy) && (
                            <>
                                <Field label="Onboarded By" value={onboardedBy || undefined} />
                                <Field label="Onboarded By Mobile" value={mobileValue(user.onboarded_by_mobile)} />
                            </>
                        )}
                    </FieldGrid>
                </Section>
            )}

            {show.has('kyc') && (
                <Section icon={ShieldCheck} title="KYC Details" action={kycActionSlot}>
                    {hasKyc ? (
                            <FieldGrid docs={<>
                                <DocTile label="Aadhaar Front" url={user.aadhar_front_image_url} onOpen={setViewingImage} />
                                <DocTile label="Aadhaar Back" url={user.aadhar_back_image_url} onOpen={setViewingImage} />
                                <DocTile label="PAN Card" url={user.panCardUrl} onOpen={setViewingImage} />
                            </>}>
                                <Field label="Date of Birth" value={fmtDate(user.dob)} />
                                <Field label="Gender" value={user.gender} />
                                <Field label="Occupation" value={user.occupation} />
                                <Field label="Aadhaar Number" value={user.aadhar_number} mono />
                                <Field label="PAN Number" value={user.panCardNumber || user.pan_number} mono />
                                <Field label="City" value={user.city} />
                                <Field label="State" value={user.state} />
                                <Field label="Pincode" value={user.pincode} />
                                <Field label="Aadhaar Address" value={user.aadhar_address} wide />
                                <Field label="Permanent Address" value={user.permanent_address || user.address} wide />
                                <Field label="Current Address" value={user.current_address} wide />
                                {showUpdated && <Field label="Updated By" value={updatedBy || undefined} />}
                                {showUpdated && <Field label="Updated Date" value={fmtDate(user.updated_date)} />}
                            </FieldGrid>
                    ) : (
                        <Note text="KYC details have not been submitted yet." />
                    )}
                </Section>
            )}

            {show.has('bank') && (
                <Section icon={Landmark} title="Bank Details" action={bankActionSlot}>
                    {hasBank ? (
                            <FieldGrid docs={<>
                                <DocTile label="Passbook Photo" url={pick('passbook_image_url')} onOpen={setViewingImage} />
                                <DocTile label="Cancelled Cheque" url={pick('cancelled_cheque_url')} onOpen={setViewingImage} />
                            </>}>
                                <Field label="Account Holder Name" value={pick('account_holder_name')} />
                                <Field label="Account Number" value={accountNumber ? <MaskedText value={String(accountNumber)} /> : undefined} />
                                <Field label="IFSC Code" value={pick('ifsc_code')} mono />
                                <Field label="Bank Name" value={pick('bank_name')} />
                            </FieldGrid>
                    ) : (
                        <Note text="No bank details on file yet." />
                    )}
                </Section>
            )}
        </div>
    );

    return (
        <>
            {variant === 'plain' ? (
                <div className={`p-6 ${className}`}>{body}</div>
            ) : (
                <div className={`w-full bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden ${className}`}>
                    {!hideHeader && (
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
                            <UserCircle2 size={24} className="text-[#4a7c59]" />
                            <h2 className="text-xl font-bold text-slate-800 uppercase tracking-tight">{title}</h2>
                            {historyButton && <div className="ml-auto">{historyButton}</div>}
                        </div>
                    )}
                    <div className="p-6">{body}</div>
                </div>
            )}
            <ImageViewerModal url={viewingImage} onClose={() => setViewingImage(null)} />
            {historyOpen && (
                <UserHistoryModal mobile={user.mobile} userName={fullName || undefined} onClose={() => setHistoryOpen(false)} />
            )}
        </>
    );
};

export default UserProfileCard;
